'use client';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@tuic/web-compositions/shared/Dialog';
import { LoadingSpinner } from '@tuic/web-compositions/core/LoadingSpinner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@tuic/web-compositions/core/Tabs';
import { useClientTranslation } from '@lib/i18n/client';
import { PhaserAttributes, PhaserClientData } from '../types';
import { useThirdPartyEvent } from '@tuic/lib/src/hooks';
import { EVENT_PHASER_OPENER } from '../config';
import { DismissedAlerts } from './dismissed-alerts';
import { JourneyPreview } from './journey-preview';
import { Locale } from '@tuic/i18n';
import { InternetCard } from '@modules/phaser/components/internet-card';
import { TransportStateForm } from '@modules/phaser/components/transit-state';
import { getPhaserAttributesFromUrl } from '@modules/phaser';

type Props = {
  getData: (attrs?: PhaserAttributes) => Promise<PhaserClientData | undefined>;
  locale: Locale;
};

export const PhaserClient: FC<Props> = ({ getData, locale }) => {
  let { t } = useClientTranslation();
  let [open, setOpen] = useState(false);
  let [data, setData] = useState<PhaserClientData>();
  let [activeTab, setActiveTab] = useState('journey');

  let { listen: openPhaserListener } = useThirdPartyEvent(EVENT_PHASER_OPENER);

  let updateData = useCallback(async () => {
    return getData().then(data => setData(data));
  }, [getData]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setData(undefined);

    const attrs = getPhaserAttributesFromUrl(window.location.href);

    getData(attrs).then(data => setData(data));
  }, [getData, open]);

  openPhaserListener(() => {
    setOpen(true);
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        title={
          typeof data === 'undefined' ? t('Laden ...') : t('Reise-Vorschau')
        }
        desktopSize="lg"
        bodyClassName="bg-background-primary text-marine-100 relative transition-all"
        nearlyFullScreen
      >
        {typeof data === 'undefined' && (
          <div className="absolute inset-0 text-center">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <LoadingSpinner size="xl" className="inline-block" />
              {t('Informationen werden gesammelt ...')}
            </div>
          </div>
        )}

        {typeof data !== 'undefined' && (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-10"
          >
            <TabsList>
              <TabsTrigger value="journey">{t('Reise-Vorschau')}</TabsTrigger>
              <TabsTrigger value="alerts">{t('Hinweise')}</TabsTrigger>
              <TabsTrigger value="internet-card">
                {t('Internet-Card')}
              </TabsTrigger>
              <TabsTrigger value="transit">{t('An- und Abreise')}</TabsTrigger>
            </TabsList>

            <TabsContent value="journey">
              <JourneyPreview data={data} />
            </TabsContent>

            <TabsContent value="alerts">
              <DismissedAlerts data={data} token={data.token} locale={locale} />
            </TabsContent>

            <TabsContent value="internet-card">
              <InternetCard data={data} token={data.token} locale={locale} />
            </TabsContent>

            <TabsContent value="transit">
              <TransportStateForm
                data={data}
                token={data.token}
                locale={locale}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
